import type * as React from 'react';
import type { ReactNode } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { IconButton } from '../icon-button/icon-button.tsx';
import { SectionLabel } from '../section-label/section-label.tsx';

export interface ChatNudgeCardProps extends Omit<
  React.ComponentProps<'div'>,
  'style' | 'children'
> {
  /** The picture for this suggestion — drawn twice: on the chip and on the action. */
  icon: ReactNode;
  /** What this is about, in a couple of words. */
  title: string;
  /** One sentence saying why it is worth doing. */
  body: string;
  /** The one thing to do about it. */
  actionLabel: string;
  onAction: () => void;
  /** Names the ✕ for a screen reader. */
  dismissLabel: string;
  onDismiss: () => void;
  /** Whether the action can still be taken — a send in flight holds it. */
  disabled?: boolean;
}

/**
 * A recommendation in an otherwise empty drawer: one thing this person has not
 * done yet, on the page where doing it makes sense.
 *
 * Not a message and never part of the conversation — it is drawn while there is
 * nothing to read, and it is gone the moment there is. Which is also why it
 * carries its own ✕: everything else in the drawer was asked for, and this was
 * not, so saying no to it has to be as easy as saying yes.
 *
 * It is shaped like a turn of the conversation all the same — a chip on the
 * left and a card beside it, on the same grid as ChatMessage — because it comes
 * from the same voice and sits in the same column. The card itself is plain
 * white: green here would colour the whole thing an invitation, and only one
 * part of it is. That part is the action at the foot, tinted and full width,
 * with a chevron saying it leads somewhere rather than submits something.
 */
export function ChatNudgeCard({
  icon,
  title,
  body,
  actionLabel,
  onAction,
  dismissLabel,
  onDismiss,
  disabled = false,
  className,
  ...props
}: ChatNudgeCardProps) {
  return (
    <div
      data-slot='chat-nudge-card'
      className={cn('flex w-full gap-2.5', className)}
      {...props}
    >
      {/*
        The avatar's place and the avatar's size: whoever reads the drawer top
        to bottom should not have to work out that this came from the same
        place the messages do.
      */}
      <span
        data-slot='chat-nudge-chip'
        aria-hidden='true'
        className='flex size-6.5 shrink-0 items-center justify-center rounded-sm bg-primary text-accent [&>svg]:size-3'
      >
        {icon}
      </span>

      <div className='relative min-w-0 flex-1 rounded-md border border-line bg-surface p-4'>
        {/*
          In the corner and out of the reading order's way: the card is read
          first and refused second, and a ✕ on its own line would weigh as much
          as the thing it dismisses.
        */}
        <IconButton
          aria-label={dismissLabel}
          size='sm'
          onClick={onDismiss}
          className='absolute top-1.5 right-1.5'
        >
          <X size={14} aria-hidden='true' />
        </IconButton>

        <SectionLabel as='h3' className='pr-7'>
          {title}
        </SectionLabel>

        <p className='mt-1.5 text-base text-ink'>{body}</p>

        {/*
          Full width, because it is the only thing to do here: a card with one
          action and room to spare reads as a card with an action hidden in it.
          The picture from the chip again, so the offer and the way to take it
          are plainly the same thing.
        */}
        <button
          data-slot='chat-nudge-action'
          type='button'
          disabled={disabled}
          onClick={onAction}
          className={cn(
            'mt-3 flex w-full cursor-pointer items-center gap-2.5 rounded-sm',
            'border border-accent-line bg-accent-tint px-3.5 py-2.25 text-left',
            'text-base font-semibold text-ink transition hover:bg-accent-line',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
            'disabled:pointer-events-none disabled:opacity-40',
          )}
        >
          <span aria-hidden='true' className='flex shrink-0 [&>svg]:size-3.25'>
            {icon}
          </span>
          <span className='min-w-0 flex-1'>{actionLabel}</span>
          <ChevronRight size={15} aria-hidden='true' className='shrink-0' />
        </button>
      </div>
    </div>
  );
}
